import React from 'react';
import { Link } from 'react-router-dom';
import FinalCTA from '../components/home/FinalCTA';

interface ResourceTeaser {
  type: string;
  title: string;
  desc: string;
  color: string;
}

const teasers: ResourceTeaser[] = [
  { type: 'Guide', title: 'CSRD readiness for non-EU companies with EU obligations', desc: 'How non-EU parent companies with EU subsidiaries determine their CSRD scope, timeline, and data requirements under the Corporate Sustainability Reporting Directive.', color: 'var(--teal)' },
  { type: 'Guide', title: 'BRSR essential vs leadership indicators: what listed companies must collect', desc: 'A structured walkthrough of SEBI\'s BRSR framework across all nine principles — which indicators are mandatory, which are voluntary, and what data collection looks like in practice.', color: 'var(--emerald)' },
  { type: 'Guide', title: 'CBAM transition to full operation: what importers need to do before 2026', desc: 'CBAM moves from transitional to full operation in 2026. This guide covers what data importers need from suppliers, how default values work, and what the declaration process looks like.', color: 'var(--amber)' },
  { type: 'Explainer', title: 'Double materiality: what it means and how to do the assessment', desc: 'CSRD requires a double materiality assessment before you can scope your ESRS disclosures. This explainer walks through the impact materiality and financial materiality lenses and what IRO identification involves.', color: 'var(--teal)' },
  { type: 'Explainer', title: 'EU Taxonomy: from eligibility to alignment', desc: 'Many companies confuse Taxonomy eligibility with Taxonomy alignment. This explainer covers the difference, what the Technical Screening Criteria and DNSH checks require, and how the KPIs are calculated.', color: 'var(--emerald)' },
  { type: 'Explainer', title: 'Scope 3 Category 1: why spend proxies are not enough for CSRD', desc: 'ESRS E1 pushes companies toward actual supplier emission data for Scope 3 Category 1. This explainer covers what the standard requires and how to structure supplier data collection.', color: 'var(--amber)' },
];

export default function Resources(): React.ReactElement {
  return (
    <main>
      <div className="page-hero">
        <div className="container">
          <span className="eyebrow" style={{ color: '#67E8F9', justifyContent: 'center', display: 'flex' }}>Resources</span>
          <h1>Guides, explainers, and practical content for ESG reporting teams.</h1>
          <p>
            Practical resources on CSRD, BRSR, EU Taxonomy, CBAM, and Scope 3 for sustainability,
            finance, and procurement professionals navigating EU and Indian regulatory obligations.
          </p>
        </div>
      </div>

      <section className="section-pad" style={{ background: 'var(--white)' }}>
        <div className="container">
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '40px', padding: '20px 28px', background: 'var(--slate-50)', borderRadius: '12px', borderLeft: '4px solid var(--amber)' }}>
            <span style={{ fontSize: '1.25rem' }}>🚧</span>
            <div>
              <p style={{ color: 'var(--navy)', fontWeight: 600, marginBottom: '4px' }}>Content coming soon</p>
              <p style={{ color: 'var(--slate-500)', fontSize: '0.9375rem' }}>
                We are publishing practical guides and explainers for ESG reporting teams. Sign up for
                updates or <Link to="/contact" style={{ color: 'var(--teal)', textDecoration: 'none', fontWeight: 500 }}>get in touch</Link> if
                you have a specific framework question.
              </p>
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '24px' }}>
            {teasers.map((r, i) => (
              <div key={i} className="card" style={{ opacity: 0.65, cursor: 'default', position: 'relative', overflow: 'hidden' }}>
                <div style={{ position: 'absolute', top: '16px', right: '16px', background: 'var(--slate-100)', color: 'var(--slate-400)', fontSize: '0.75rem', fontWeight: 600, padding: '3px 10px', borderRadius: '20px', letterSpacing: '0.04em' }}>
                  Coming soon
                </div>
                <div style={{ display: 'inline-block', background: r.color + '1A', color: r.color, fontSize: '0.75rem', fontWeight: 700, padding: '3px 10px', borderRadius: '20px', letterSpacing: '0.04em', marginBottom: '12px' }}>
                  {r.type}
                </div>
                <h4 style={{ color: 'var(--navy)', marginBottom: '10px', paddingRight: '48px', fontSize: '1rem', lineHeight: 1.45 }}>{r.title}</h4>
                <p style={{ color: 'var(--slate-500)', fontSize: '0.9rem', lineHeight: 1.65 }}>{r.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="section-pad" style={{ background: 'var(--slate-50)' }}>
        <div className="container" style={{ textAlign: 'center', maxWidth: '600px' }}>
          <h2 style={{ color: 'var(--navy)', marginBottom: '16px' }}>Have a framework question?</h2>
          <p style={{ color: 'var(--slate-500)', lineHeight: 1.75, marginBottom: '28px' }}>
            If you are working through a specific BRSR, CSRD, or CBAM question and want a practical
            conversation, reach out to us directly. We are happy to discuss your situation.
          </p>
          <div style={{ display: 'flex', gap: '16px', justifyContent: 'center', flexWrap: 'wrap' }}>
            <Link to="/contact" className="btn btn-primary">Talk to us</Link>
            <a href="mailto:hello@esgtriage.com" className="btn btn-ghost-dark">hello@esgtriage.com</a>
          </div>
        </div>
      </section>

      <FinalCTA />
    </main>
  );
}
